import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, ArrowLeft, Building2, User } from "lucide-react";

const B = {
  border: "1px solid rgba(45,53,97,0.08)",
  shadow: "none",
  shadowSm: "none",
  font: "'Instrument Serif', Georgia, serif",
  mono: "'IBM Plex Mono', monospace",
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function ConversationList({ conversations, activeId, onSelect, userId }) {
  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 px-4">
        <MessageCircle className="w-10 h-10 mb-3" style={{ color: "#ccc" }} />
        <p className="text-sm text-center" style={{ color: "#999" }}>Aún no tienes conversaciones.</p>
        <p className="text-xs text-center mt-1" style={{ fontFamily: B.mono, color: "#ccc" }}>
          // OFRECE AYUDA PARA INICIAR UN CHAT
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="conversation-list">
      {conversations.map((conv) => {
        const otherId = conv.participants.find((p) => p !== userId);
        const otherInfo = conv.participant_info?.[otherId] || {};
        const isActive = conv.conversation_id === activeId;
        const RoleIcon = otherInfo.role === "protectora" ? Building2 : User;

        return (
          <button
            key={conv.conversation_id}
            onClick={() => onSelect(conv.conversation_id)}
            className="w-full text-left px-4 py-3 transition-all"
            style={{
              background: isActive ? "#686af315" : "transparent",
              borderBottom: "1px solid rgba(45,53,97,0.06)",
              borderLeft: isActive ? "2px solid #4A90D9" : "2px solid transparent",
            }}
            data-testid={`conv-item-${conv.conversation_id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                style={{ background: otherInfo.role === "protectora" ? "#a88efb20" : "#686af320", border: "1px solid rgba(45,53,97,0.08)", borderRadius: 4 }}>
                {otherInfo.picture ? (
                  <img src={otherInfo.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <RoleIcon className="w-4 h-4" style={{ color: otherInfo.role === "protectora" ? "#a88efb" : "#686af3" }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold truncate" style={{ color: "#29294d" }}>{otherInfo.name || "Usuario"}</p>
                  <span className="text-[10px] flex-shrink-0 ml-2" style={{ fontFamily: B.mono, color: "#ccc" }}>{timeAgo(conv.last_message_at)}</span>
                </div>
                {conv.need_title && (
                  <p className="text-[10px] truncate" style={{ fontFamily: B.mono, color: "#686af3" }}>{conv.need_title}</p>
                )}
                <p className="text-xs truncate mt-0.5" style={{ color: "#999" }}>{conv.last_message || "Sin mensajes"}</p>
              </div>
              {conv.unread > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 text-white flex-shrink-0" style={{ fontFamily: B.mono, background: "#686af3" }}>
                  {conv.unread}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function MessageThread({ conversationId, userId, otherInfo }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMessages = useCallback(() => {
    if (!conversationId) return;
    axios.get(`${API}/messages/${conversationId}`, { headers, withCredentials: true }).then((r) => setMessages(r.data)).catch(() => {});
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchMessages(); const interval = setInterval(fetchMessages, 3000); return () => clearInterval(interval); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { conversation_id: conversationId, text: text.trim() }, { headers, withCredentials: true });
      setText("");
      fetchMessages();
    } catch {}
    setSending(false);
  };

  if (!conversationId) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <MessageCircle className="w-12 h-12 mb-3" style={{ color: "#ccc" }} />
        <p className="text-base font-bold" style={{ fontFamily: B.font, color: "#ccc" }}>SELECCIONA UNA CONVERSACIÓN</p>
        <p className="text-xs mt-1" style={{ fontFamily: B.mono, color: "#ddd" }}>// ELIGE UN CHAT DE LA LISTA</p>
      </div>
    );
  }

  const RoleIcon = otherInfo?.role === "protectora" ? Building2 : User;

  return (
    <div className="flex flex-col h-full" data-testid="message-thread">
      {/* Header */}
      <div className="px-5 py-3 flex items-center gap-3 flex-shrink-0" style={{ borderBottom: "1px solid rgba(45,53,97,0.08)" }}>
        <div className="w-9 h-9 flex items-center justify-center"
          style={{ background: otherInfo?.role === "protectora" ? "#a88efb20" : "#686af320", border: "1px solid rgba(45,53,97,0.08)", borderRadius: 4 }}>
          {otherInfo?.picture ? (
            <img src={otherInfo.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <RoleIcon className="w-4 h-4" style={{ color: otherInfo?.role === "protectora" ? "#a88efb" : "#686af3" }} />
          )}
        </div>
        <div>
          <p className="text-sm font-bold" style={{ fontFamily: B.font, color: "#29294d" }}>{otherInfo?.name || "Chat"}</p>
          <p className="text-[10px]" style={{ fontFamily: B.mono, color: "#a88efb" }}>
            {otherInfo?.role === "protectora" ? "PROTECTORA" : "VOLUNTARIO"}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ background: "#f0eee9" }}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm" style={{ fontFamily: B.mono, color: "#ccc" }}>// INICIA LA CONVERSACIÓN</p>
          </div>
        )}
        {messages.map((msg) => {
          const isMine = msg.sender_id === userId;
          return (
            <div key={msg.message_id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.message_id}`}>
              <div className="max-w-[75%] px-4 py-2.5"
                style={{
                  background: isMine ? "#686af3" : "#fff",
                  color: isMine ? "#fff" : "#29294d",
                  border: "1px solid rgba(45,53,97,0.08)", borderRadius: 4,
                }}>
                {!isMine && (
                  <p className="text-[10px] font-bold mb-0.5" style={{ fontFamily: B.mono, color: "#a88efb" }}>{msg.sender_name}</p>
                )}
                <p className="text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{msg.text}</p>
                <p className="text-[9px] mt-1" style={{ fontFamily: B.mono, color: isMine ? "rgba(255,255,255,0.5)" : "#ccc" }}>
                  {new Date(msg.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="px-5 py-3 flex items-center gap-2 flex-shrink-0" style={{ borderTop: "1px solid rgba(45,53,97,0.08)", background: "#fff" }}>
        <Input
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Escribe un mensaje..."
          className="flex-1"
          style={{ border: "1px solid rgba(45,53,97,0.08)", borderRadius: 4, fontFamily: "'Inter', sans-serif" }}
          data-testid="chat-message-input"
        />
        <button
          type="submit" disabled={!text.trim() || sending}
          className="w-10 h-10 flex items-center justify-center flex-shrink-0 text-white transition-all duration-150 hover:-translate-y-0.5"
          style={{ background: "#686af3", border: "1px solid rgba(45,53,97,0.08)", borderRadius: 4 }}
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

export default function ChatPanel({ className = "" }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConversations = useCallback(() => {
    if (!user) return;
    axios.get(`${API}/conversations`, { headers, withCredentials: true }).then((r) => setConversations(r.data)).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchConversations(); const interval = setInterval(fetchConversations, 5000); return () => clearInterval(interval); }, [fetchConversations]);

  const activeConv = conversations.find((c) => c.conversation_id === activeConvId);
  const otherId = activeConv?.participants?.find((p) => p !== user?.user_id);
  const otherInfo = activeConv?.participant_info?.[otherId];
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread || 0), 0);

  const handleSelectConv = (convId) => { setActiveConvId(convId); setMobileShowThread(true); };

  return (
    <div className={`bg-white overflow-hidden flex ${className}`} style={{ height: 520, border: B.border, boxShadow: B.shadow }} data-testid="chat-panel">
      {/* Conversation list */}
      <div className={`w-full md:w-80 flex flex-col ${mobileShowThread ? "hidden md:flex" : "flex"}`} style={{ borderRight: "1px solid rgba(45,53,97,0.08)" }}>
        <div className="px-4 py-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: "1px solid rgba(45,53,97,0.08)" }}>
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" style={{ color: "#686af3" }} />
            <h3 className="text-sm font-bold" style={{ fontFamily: B.font, color: "#29294d" }}>MENSAJES</h3>
          </div>
          {totalUnread > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 text-white" style={{ fontFamily: B.mono, background: "#686af3" }}>{totalUnread}</span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          <ConversationList conversations={conversations} activeId={activeConvId} onSelect={handleSelectConv} userId={user?.user_id} />
        </div>
      </div>

      {/* Message thread */}
      <div className={`flex-1 flex flex-col ${!mobileShowThread ? "hidden md:flex" : "flex"}`}>
        {mobileShowThread && (
          <button onClick={() => setMobileShowThread(false)}
            className="md:hidden flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-wider"
            style={{ fontFamily: B.mono, color: "#686af3", borderBottom: "1px solid rgba(45,53,97,0.08)" }}
            data-testid="chat-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> VOLVER
          </button>
        )}
        <MessageThread conversationId={activeConvId} userId={user?.user_id} otherInfo={otherInfo} />
      </div>
    </div>
  );
}
